import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Card, Row, Col, Statistic, Typography, DatePicker, Select, Spin, Table, Tag, Divider, Space
} from 'antd';
import {
    BarChartOutlined, DollarOutlined, ShoppingCartOutlined, RiseOutlined,
    FallOutlined, WarningOutlined, AppstoreOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PharmacyReports = () => {
    const { t } = useTranslation(['pharmacy', 'common']);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState([
        dayjs().startOf('month'),
        dayjs().endOf('month')
    ]);

    // Report data
    const [salesReport, setSalesReport] = useState(null);
    const [inventoryReport, setInventoryReport] = useState(null);
    const [profitReport, setProfitReport] = useState(null);
    const [returnsData, setReturnsData] = useState([]);
    const [expenseStats, setExpenseStats] = useState(null);

    useEffect(() => {
        fetchAllReports();
    }, [dateRange]);

    const fetchAllReports = async () => {
        try {
            setLoading(true);
            const startDate = dateRange[0].format('YYYY-MM-DD');
            const endDate = dateRange[1].format('YYYY-MM-DD');

            const [salesRes, inventoryRes, profitRes, returnsRes, expenseRes] = await Promise.all([
                api.get(`/pharmacy/reports/sales?startDate=${startDate}&endDate=${endDate}`),
                api.get('/pharmacy/reports/inventory'),
                api.get(`/pharmacy/reports/profit-loss?startDate=${startDate}&endDate=${endDate}`),
                api.get(`/pharmacy/returns?startDate=${startDate}&endDate=${endDate}&limit=100`),
                api.get(`/expenses/stats?startDate=${startDate}&endDate=${endDate}`).catch(() => ({ data: { data: null } }))
            ]);

            setSalesReport(salesRes.data?.data || null);
            setInventoryReport(inventoryRes.data?.data || null);
            setProfitReport(profitRes.data?.data || null);
            setReturnsData(returnsRes.data?.data || []);
            setExpenseStats(expenseRes.data?.data || null);
        } catch (error) {
            console.error('Error fetching reports:', error);
        } finally {
            setLoading(false);
        }
    };

    // Split sales by currency
    const salesByCurrency = (salesReport?.sales || []).reduce((acc, sale) => {
        const c = sale.currency || 'USD';
        if (!acc[c]) acc[c] = { count: 0, revenue: 0, paid: 0, pending: 0 };
        acc[c].count += 1;
        acc[c].revenue += sale.totalAmount || 0;
        acc[c].paid += sale.amountPaid || 0;
        acc[c].pending += sale.balanceDue || 0;
        return acc;
    }, {});

    // Returns summary
    const totalRefunds = returnsData.reduce((sum, r) => sum + (r.totalRefundAmount || 0), 0);

    // Expense totals
    const totalExpenses = expenseStats?.totalAmount || 0;

    const fmt = (amount, currency) =>
        currency === 'IQD' ? `IQD ${Math.round(amount).toLocaleString()}` : `$${Number(amount).toFixed(2)}`;

    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: 100 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    <Title level={2} style={{ margin: 0 }}><BarChartOutlined /> {t('pharmacy:reports.title')}</Title>
                </Col>
                <Col>
                    <RangePicker
                        value={dateRange}
                        onChange={(dates) => dates && setDateRange(dates)}
                        style={{ width: 280 }}
                    />
                </Col>
            </Row>

            {/* Sales Overview */}
            <Title level={4}>{t('pharmacy:reports.salesOverview')}</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.totalSales')}
                            value={salesReport?.totalSales || 0}
                            prefix={<ShoppingCartOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                {Object.entries(salesByCurrency).map(([currency, data]) => (
                    <React.Fragment key={currency}>
                        <Col xs={12} sm={8}>
                            <Card>
                                <Statistic
                                    title={t('pharmacy:reports.revenue', { currency })}
                                    value={data.revenue}
                                    prefix={<DollarOutlined />}
                                    valueStyle={{ color: '#52c41a' }}
                                    formatter={() => fmt(data.revenue, currency)}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8}>
                            <Card>
                                <Statistic
                                    title={t('pharmacy:reports.collected', { currency })}
                                    value={data.paid}
                                    prefix={<DollarOutlined />}
                                    valueStyle={{ color: '#722ed1' }}
                                    formatter={() => fmt(data.paid, currency)}
                                />
                            </Card>
                        </Col>
                    </React.Fragment>
                ))}
            </Row>

            {/* Profit & Loss */}
            <Title level={4}>{t('pharmacy:reports.profitLoss')}</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.totalCost')}
                            value={profitReport?.totalCost || 0}
                            prefix={<FallOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                            precision={2}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.totalRevenue')}
                            value={profitReport?.totalRevenue || 0}
                            prefix={<RiseOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                            precision={2}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.grossProfit')}
                            value={profitReport?.profit || 0}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: (profitReport?.profit || 0) >= 0 ? '#52c41a' : '#ff4d4f' }}
                            precision={2}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.profitMargin')}
                            value={profitReport?.profitMargin || '0%'}
                            prefix={<RiseOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Returns & Expenses */}
            <Title level={4}>{t('pharmacy:reports.returnsExpenses')}</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={8}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.totalReturns')}
                            value={returnsData.length}
                            prefix={<FallOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.totalRefunded')}
                            value={totalRefunds}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                            precision={2}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.totalExpenses')}
                            value={totalExpenses}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                            precision={2}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Net Summary */}
            <Card style={{ marginBottom: 24, background: '#f6ffed', borderColor: '#b7eb8f' }}>
                <Row gutter={16} justify="center">
                    <Col xs={12} sm={8}>
                        <Statistic
                            title={t('pharmacy:reports.netProfit')}
                            value={
                                (profitReport?.profit || 0) - totalExpenses - totalRefunds
                            }
                            prefix={<DollarOutlined />}
                            valueStyle={{
                                color: ((profitReport?.profit || 0) - totalExpenses - totalRefunds) >= 0 ? '#52c41a' : '#ff4d4f',
                                fontSize: 28
                            }}
                            precision={2}
                        />
                    </Col>
                </Row>
            </Card>

            {/* Inventory Status */}
            <Title level={4}>{t('pharmacy:reports.inventoryStatus')}</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.totalItems')}
                            value={inventoryReport?.totalItems || 0}
                            prefix={<AppstoreOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.lowStock')}
                            value={inventoryReport?.lowStockItems || 0}
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.expiringSoon')}
                            value={inventoryReport?.expiringItems || 0}
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:reports.inventoryValue')}
                            value={inventoryReport?.totalInventoryValue || 0}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                            precision={2}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Low Stock Items Table */}
            {inventoryReport?.lowStockList?.length > 0 && (
                <Card title={t('pharmacy:reports.lowStockItems')} size="small" style={{ marginBottom: 16 }}>
                    <Table
                        dataSource={inventoryReport.lowStockList}
                        rowKey="_id"
                        size="small"
                        pagination={false}
                        columns={[
                            { title: t('pharmacy:common.item'), dataIndex: 'itemName', key: 'itemName' },
                            { title: t('pharmacy:common.sku'), dataIndex: 'sku', key: 'sku', width: 100 },
                            {
                                title: t('pharmacy:common.stock'), dataIndex: 'quantity', key: 'quantity', width: 80,
                                render: (q) => <Tag color="orange">{q}</Tag>
                            },
                            { title: t('pharmacy:reports.reorderLevel'), dataIndex: 'reorderLevel', key: 'reorderLevel', width: 110 },
                            { title: t('pharmacy:common.category'), dataIndex: 'category', key: 'category', width: 120 }
                        ]}
                    />
                </Card>
            )}

            {/* Expiring Items Table */}
            {inventoryReport?.expiringList?.length > 0 && (
                <Card title={t('pharmacy:reports.expiringSoonItems')} size="small" style={{ marginBottom: 16 }}>
                    <Table
                        dataSource={inventoryReport.expiringList}
                        rowKey="_id"
                        size="small"
                        pagination={false}
                        columns={[
                            { title: t('pharmacy:common.item'), dataIndex: 'itemName', key: 'itemName' },
                            {
                                title: t('pharmacy:reports.expiryDate'), dataIndex: 'expiryDate', key: 'expiryDate', width: 120,
                                render: (d) => <Tag color="red">{dayjs(d).format('YYYY-MM-DD')}</Tag>
                            },
                            { title: t('pharmacy:common.stock'), dataIndex: 'quantity', key: 'quantity', width: 80 },
                            { title: t('pharmacy:common.category'), dataIndex: 'category', key: 'category', width: 120 }
                        ]}
                    />
                </Card>
            )}

            {/* Top Sold Items */}
            {salesReport?.sales?.length > 0 && (
                <Card title={t('pharmacy:reports.topSoldItems')} size="small">
                    <Table
                        dataSource={getTopItems(salesReport.sales)}
                        rowKey="itemName"
                        size="small"
                        pagination={false}
                        columns={[
                            { title: '#', render: (_, __, i) => i + 1, width: 40 },
                            { title: t('pharmacy:common.item'), dataIndex: 'itemName', key: 'itemName' },
                            { title: t('pharmacy:reports.qtySold'), dataIndex: 'totalQty', key: 'totalQty', width: 100, sorter: (a, b) => a.totalQty - b.totalQty, defaultSortOrder: 'descend' },
                            {
                                title: t('pharmacy:reports.revenueCol'), dataIndex: 'totalRevenue', key: 'totalRevenue', width: 130,
                                render: (v) => `$${v.toFixed(2)}`
                            }
                        ]}
                    />
                </Card>
            )}
        </div>
    );
};

// Helper: aggregate top sold items from sales
const getTopItems = (sales) => {
    const map = {};
    for (const sale of sales) {
        for (const item of sale.items || []) {
            if (!map[item.itemName]) {
                map[item.itemName] = { itemName: item.itemName, totalQty: 0, totalRevenue: 0 };
            }
            map[item.itemName].totalQty += item.quantity;
            map[item.itemName].totalRevenue += item.total;
        }
    }
    return Object.values(map).sort((a, b) => b.totalQty - a.totalQty).slice(0, 15);
};

export default PharmacyReports;
